import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Gestion de trésorerie : guide complet pour ne jamais manquer de cash | Trezoone',
    description: 'Apprenez à gérer votre trésorerie efficacement. Anticiper les tensions, piloter vos flux, éviter les ruptures. Guide concret pour associations, TPE et PME.',
    canonical: '/gestion-tresorerie',
    keywords: 'gestion trésorerie, gérer trésorerie, pilotage trésorerie, trésorerie entreprise, flux de trésorerie',
  },
  hero: {
    tag: 'Gestion de trésorerie',
    title: 'Si rien ne rentre pendant 2 mois, combien de temps vous tenez ?',
    subtitle: 'La gestion de trésorerie, ce n\'est pas de la comptabilité. C\'est savoir si votre compte en banque tiendra jusqu\'à la fin du mois — et agir avant que ça coince.',
    ctaLabel: 'Calculer ma survie financière',
    ctaHref: '/simulateur-tresorerie-association',
  },
  sections: [
    {
      type: 'hook',
      title: 'Le vrai problème des dirigeants et trésoriers',
      content: 'La plupart des structures saines sur le papier meurent d\'un problème de trésorerie. Pas de faillite spectaculaire. Juste : les charges arrivent, les recettes traînent, et le compte plonge.',
      items: [
        'Une subvention de 30 000 € attendue en mars… versée en juin. Entre temps, les salaires n\'attendent pas.',
        'Un client facturé en octobre qui paie à 60 jours. Vous, vous avez des charges le 5 du mois.',
        'Un mois de trésorerie positive qui masque 3 mois de tension à venir.',
      ],
    },
    {
      type: 'explain',
      title: 'Trésorerie vs comptabilité : la différence qui sauve',
      content: 'Votre comptable vous dit que vous êtes bénéficiaire. Et pourtant votre compte est vide. Pourquoi ? Parce que la comptabilité enregistre quand la charge est engagée. La trésorerie, c\'est quand le cash bouge réellement.',
      items: [
        'Comptabilité : vous facturez en octobre → produit comptabilisé en octobre',
        'Trésorerie : le client paie en décembre → cash reçu en décembre',
        'Le décalage entre les deux, c\'est votre besoin de trésorerie à financer',
        'Un résultat positif ne garantit pas un solde positif — c\'est le piège n°1',
      ],
    },
    {
      type: 'example',
      title: 'Cas concret : une association bien gérée en danger',
      content: 'L\'association "Sport Pour Tous" a un budget de 150 000 €/an. Résultat prévisionnel : +5 000 €. Tout semble OK. Mais :',
      items: [
        'Janvier → Mars : charges de personnel 12 000 €/mois, subvention CAF attendue mais pas encore versée',
        'Résultat : compte bancaire à -8 000 € en mars malgré un exercice bénéficiaire',
        'Avec un plan de trésorerie, ce creux était visible en octobre — et finançable à temps',
        'Sans anticipation : découvert bancaire, agios, tensions avec les fournisseurs',
      ],
    },
    {
      type: 'midcta',
      title: 'Anticipez vos tensions de trésorerie dès aujourd\'hui',
      content: 'Essai gratuit 14 jours · Sans carte bancaire · Opérationnel en 30 min',
    },
    {
      type: 'solution',
      title: 'Comment Trezoone vous aide à piloter votre trésorerie',
      content: 'Trezoone transforme vos données bancaires réelles en tableau de bord de trésorerie. Vous voyez vos flux passés, vos prévisions futures, et les mois à risque — avant qu\'ils n\'arrivent.',
      items: [
        'Plan de trésorerie prévisionnel sur 12 mois, mis à jour automatiquement',
        'Suivi des subventions et alertes en cas de retard de versement',
        'Indicateur de survie financière (combien de mois vous tenez à trésorerie constante)',
        'Simulation de scénarios : que se passe-t-il si une recette est retardée de 2 mois ?',
        'Import automatique depuis vos relevés bancaires (CSV, OFX, PDF)',
        'Tableau de bord lisible par votre CA, vos financeurs ou votre banque',
      ],
    },
    {
      type: 'faq',
      title: 'Questions fréquentes sur la gestion de trésorerie',
      faq: [
        {
          q: 'Quelle est la différence entre trésorerie et résultat ?',
          a: 'Le résultat est une notion comptable (produits - charges sur une période). La trésorerie, c\'est le cash réellement disponible sur votre compte. Une structure peut avoir un résultat positif et manquer de trésorerie si les encaissements arrivent après les décaissements.',
        },
        {
          q: 'À quelle fréquence faut-il gérer sa trésorerie ?',
          a: 'Idéalement chaque semaine pour les structures avec des flux importants, une fois par mois au minimum. L\'essentiel est d\'avoir une projection à 3-6 mois pour identifier les tensions à venir.',
        },
        {
          q: 'Un tableur Excel suffit-il pour gérer sa trésorerie ?',
          a: 'Excel peut suffire dans un premier temps, mais il devient vite un frein : erreurs de formules, données non à jour, pas de lien avec la banque, aucune alerte automatique. Un outil dédié comme Trezoone évite ces écueils.',
        },
        {
          q: 'Comment anticiper un manque de trésorerie ?',
          a: 'En construisant un plan de trésorerie prévisionnel : listez mois par mois vos encaissements attendus et vos décaissements prévus. Les mois où le solde cumulé devient négatif sont vos zones de risque — à financer en avance.',
        },
        {
          q: 'Trezoone convient-il aux associations et aux PME ?',
          a: 'Oui. Trezoone est conçu pour les associations (suivi de subventions, gestion des crédits), les TPE/PME (BFR, retards clients) et les structures agricoles. Chaque profil a ses propres modules.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Approfondir par profil et par besoin',
      links: [
        { label: 'Gestion trésorerie association', href: '/gestion-tresorerie-association' },
        { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
        { label: 'Gestion trésorerie TPE', href: '/gestion-tresorerie-tpe' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
        { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
        { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
        { label: 'BFR trésorerie', href: '/bfr-tresorerie' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'Prêt à ne plus jamais être surpris par votre trésorerie ?',
      content: '14 jours d\'essai gratuit. Importez vos données en 5 minutes. Visualisez vos tensions à venir.',
    },
  ],
  relatedLinks: [
    { label: 'Simulateur de trésorerie', href: '/simulateur-tresorerie-association' },
    { label: 'Prévision trésorerie', href: '/fonctionnalites/prevision-tresorerie' },
    { label: 'Stress test trésorerie', href: '/fonctionnalites/stress-test-tresorerie' },
    { label: 'Alertes trésorerie', href: '/fonctionnalites/alertes-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function GestionTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
